import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatError } from "../lib/api";
import { shuffle } from "../lib/shuffle";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Trophy, RotateCcw } from "lucide-react";
import ExplanationsPanel from "../components/ExplanationsPanel";

export default function KnowledgeTest() {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [displayName, setDisplayName] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!user) { setChecking(false); setCompleted(false); return; }
    setChecking(true);
    api.get("/quiz/knowledge/completed")
      .then((r) => setCompleted(!!r.data.completed))
      .catch(() => setCompleted(false))
      .finally(() => setChecking(false));
  }, [user]);

  useEffect(() => {
    api.get(`/quiz/knowledge?lang=${lang}`).then((r) => {
      setMeta(r.data);
      const qs = shuffle(r.data.questions).map((q) => ({
        ...q,
        displayOptions: shuffle(q.options.map((text, origIndex) => ({ text, origIndex }))),
      }));
      setQuestions(qs);
    });
  }, [lang]);

  const answeredCount = Object.keys(answers).length;
  const q = questions[idx];
  const progress = questions.length ? (answeredCount / questions.length) * 100 : 0;

  const choose = (optIndex) => setAnswers((a) => ({ ...a, [q.id]: String(optIndex) }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/knowledge/submit", { answers, display_name: displayName || undefined });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <KnowledgeResult result={result} user={user} onRetake={() => { setResult(null); setAnswers({}); setIdx(0); }} />;

  if (checking) return <div className="min-h-[60vh] grid place-items-center text-slate-400 font-mono text-sm">{t("quiz.loading")}</div>;

  if (user && completed) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10" data-testid="knowledge-answers-view">
        <div className="mb-4">
          <div className="font-mono text-xs uppercase tracking-widest text-teal-700 mb-2">{t("knowledge.kicker")}</div>
          <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">{meta?.title || t("expl.answersOnlyTitle")}</h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed bg-teal-50 border border-teal-100 rounded-lg px-4 py-3">{t("expl.knowledgeDone")}</p>
        </div>
        <ExplanationsPanel quiz="knowledge" defaultOpen />
      </div>
    );
  }

  if (!q) return <div className="min-h-[60vh] grid place-items-center text-slate-400 font-mono text-sm">{t("quiz.loading")}</div>;

  const isLast = idx === questions.length - 1;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-teal-700 mb-2">{t("knowledge.kicker")}</div>
        <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">{meta?.title}</h1>
        <p className="mt-3 text-sm text-slate-500 leading-relaxed whitespace-pre-line">{meta?.description}</p>
        {meta && <p data-testid="knowledge-stats" className="mt-3 text-xs font-mono text-teal-700">{t("expl.stats", { l: meta.lessons ?? meta.count, v: meta.videos ?? 0 })}</p>}
      </div>

      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur py-3 -mx-6 px-6 border-b border-slate-100">
        <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-2">
          <span>{t("quiz.questionOf", { n: idx + 1, t: questions.length })}</span>
          <span>{t("quiz.answered", { n: answeredCount })}</span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
          className="mt-8" data-testid="question-card"
        >
          <div className="flex items-baseline gap-3">
            <span className="font-serif font-bold text-2xl text-teal-600">{idx + 1}.</span>
            <h2 className="font-serif font-semibold text-xl sm:text-2xl text-slate-900 leading-snug">{q.text}</h2>
          </div>

          <div className="mt-6 space-y-3">
            {q.displayOptions.map((opt, i) => {
              const selected = answers[q.id] === String(opt.origIndex);
              return (
                <button
                  key={opt.origIndex}
                  data-testid={`answer-option-${i}`}
                  onClick={() => choose(opt.origIndex)}
                  className={`w-full text-left flex items-center gap-4 rounded-xl border p-4 transition-all ${
                    selected ? "border-teal-500 bg-teal-50 ring-1 ring-teal-500" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <span className={`grid place-items-center w-7 h-7 rounded-full text-sm font-mono shrink-0 border ${selected ? "bg-teal-600 text-white border-teal-600" : "border-slate-300 text-slate-500"}`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className={`text-[15px] ${selected ? "text-slate-900 font-medium" : "text-slate-700"}`}>{opt.text}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="mt-10 flex items-center justify-between gap-4">
        <Button variant="outline" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} data-testid="prev-question-btn" className="rounded-full">
          <ArrowLeft size={16} className="mr-2" /> {t("quiz.previous")}
        </Button>

        {isLast ? (
          <div className="flex flex-col items-end gap-2">
            {!user && (
              <Input
                data-testid="anon-display-name"
                placeholder={t("quiz.nameOptional")}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-48 h-9 text-sm"
              />
            )}
            <Button onClick={submit} disabled={submitting} data-testid="quiz-submit-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-7">
              {submitting ? t("quiz.scoring") : t("quiz.submit")}
            </Button>
          </div>
        ) : (
          <Button onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))} data-testid="next-question-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
            {t("quiz.next")} <ArrowRight size={16} className="ml-2" />
          </Button>
        )}
      </div>

      {!user && (
        <p className="mt-6 text-center text-xs text-slate-400">
          <Link to="/login" className="text-teal-700 underline">{t("quiz.loginHint")}</Link>{t("quiz.loginHintTail")}
        </p>
      )}
    </div>
  );
}

function KnowledgeResult({ result, user, onRetake }) {
  const { t } = useLanguage();
  const { score, total, percentage } = result;
  let tier = t("knowledge.tier.beginner");
  if (score >= 80) tier = t("knowledge.tier.master");
  else if (score >= 55) tier = t("knowledge.tier.trainer");
  else if (score >= 25) tier = t("knowledge.tier.practitioner");

  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <div data-testid="score-result-badge" className="inline-grid place-items-center w-40 h-40 rounded-full bg-white border-4 border-teal-500 shadow-lg mx-auto">
          <div>
            <div className="font-serif font-bold text-5xl text-slate-900">{score}</div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-400">/ {total}</div>
          </div>
        </div>
      </motion.div>
      <div className="mt-6 inline-block font-mono text-xs uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-4 py-1.5">
        {tier} · {percentage}%
      </div>
      <h1 className="mt-6 font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
        {t("knowledge.scored", { s: score, t: total })}
      </h1>
      <p className="mt-4 text-slate-600 leading-relaxed">
        {user ? t("knowledge.recorded") : t("knowledge.recordedAnon")}
      </p>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/leaderboards">
          <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6" data-testid="view-leaderboard-btn">
            <Trophy size={16} className="mr-2" /> {t("knowledge.viewLeaderboard")}
          </Button>
        </Link>
        <Button variant="outline" onClick={onRetake} className="rounded-full px-6" data-testid="retake-btn" style={user ? { display: "none" } : undefined}>
          <RotateCcw size={16} className="mr-2" /> {t("knowledge.retake")}
        </Button>
        {!user && (
          <Link to="/login"><Button variant="outline" className="rounded-full px-6">{t("common.register")}</Button></Link>
        )}
      </div>

      <ExplanationsPanel quiz="knowledge" breakdown={result.breakdown} submissionId={result.submission_id} />
    </div>
  );
}
